// src/firebase.js
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { getFirestore } from "firebase/firestore";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, serverTimestamp } from "firebase/firestore";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const analytics = typeof window !== "undefined" ? getAnalytics(app) : null;

export const trackEvent = (eventName, params = {}) => {
  if (analytics) logEvent(analytics, eventName, params);
};

// Authentication helper functions
export const loginWithEmail = (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const registerWithEmail = async (email, password, username) => {
  // First create the user account
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password,
  );

  // Check if this is the first user - make them admin
  const usersRef = collection(db, "users");
  const snapshot = await getDocs(usersRef);
  const isFirstUser = snapshot.empty;
  
  // Then store the additional username in Firestore
  const userRef = doc(db, "users", userCredential.user.uid);
  await setDoc(userRef, {
    email: email,
    username: username,
    createdAt: new Date(),
    termsAcceptedAt: serverTimestamp(),
    role: isFirstUser ? "admin" : "user" // First user gets admin role
  });

  return userCredential;
};

// Get user profile data
export const getUserProfile = async (userId) => {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return userSnap.data();
  }

  return null;
};

// Get all users for admin page
export const getAllUsers = async () => {
  const usersRef = collection(db, "users");
  const usersSnap = await getDocs(usersRef);

  return usersSnap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

// Update user role
export const updateUserRole = async (userId, newRole) => {
  const userRef = doc(db, "users", userId);
  await updateDoc(userRef, { role: newRole });
  return true;
};

// Update user elite flag
export const updateUserElite = async (userId, isElite) => {
  const userRef = doc(db, "users", userId);
  await updateDoc(userRef, { elite: isElite });
};

export const logout = () => {
  return signOut(auth);
};

export { db, auth, analytics };
